import { FormProvider } from '@conform-to/react';
import {
  Children,
  type FormEvent,
  type ReactElement,
  type ReactNode,
  cloneElement,
  createContext,
  isValidElement,
  useContext,
} from 'react';
import type { ZodTypeAny } from 'zod';
import { getFormProps } from './getFormProps';
import { cn } from '../../util';
import { type UseZodFormOptions, useZodForm } from './useZodForm';

export type ZodForm<Schema extends ZodTypeAny = ZodTypeAny> = ReturnType<typeof useZodForm<Schema>>;

export interface FormProps<Schema extends ZodTypeAny> extends UseZodFormOptions<Schema> {
  /**
   * Render the child element (e.g. `fetcher.Form` or Remix `Form`) as the form instead of a plain `<form>`.
   * Conform owns the child's `id`, `noValidate` and submit handling; the child's own `onSubmit` only runs for
   * submissions that pass validation.
   */
  asChild?: boolean;
  className?: string;
  /** A form-level error from the action that isn't part of Conform's result, shown by `FormError`. */
  error?: string | null;
  children?: ReactNode | ((form: { form: ZodForm<Schema>[0]; fields: ZodForm<Schema>[1] }) => ReactNode);
}

type FormElementProps = { id?: string; className?: string; onSubmit?: (event: FormEvent<HTMLFormElement>) => void };

const FormLevelErrorContext = createContext<string | null | undefined>(undefined);

export const useFormLevelError = () => useContext(FormLevelErrorContext);

/**
 * Creates a Conform form and provides it to the field components below. Use `useZodForm` + `FormProvider`
 * directly when the owning component needs the form state (e.g. to update dependent fields).
 */
export function Form<Schema extends ZodTypeAny>({
  asChild,
  className,
  error,
  children,
  id,
  ...options
}: FormProps<Schema>) {
  // Note: an `id` on the child form is honoured, so Conform, the child and `form=` attributes all agree on one id
  const childId =
    asChild && isValidElement<FormElementProps>(children) ? (children.props.id as string | undefined) : undefined;
  const [form, fields] = useZodForm<Schema>({ ...options, id: id ?? childId });
  const formProps = getFormProps(form);
  const content = typeof children === 'function' ? children({ form, fields }) : children;

  let formElement: ReactNode;
  if (asChild) {
    const child = Children.only(content) as ReactElement<FormElementProps>;
    if (process.env.NODE_ENV !== 'production' && child.props.id && child.props.id !== form.id) {
      console.error(`Form: the child form's id "${child.props.id}" is replaced by "${form.id}"; pass the id to <Form>`);
    }
    formElement = cloneElement(child, {
      ...formProps,
      className: cn(className, child.props.className),
      onSubmit: (event: FormEvent<HTMLFormElement>) => {
        formProps.onSubmit(event);
        if (!event.defaultPrevented) child.props.onSubmit?.(event);
      },
    });
  } else {
    formElement = (
      <form {...formProps} className={className}>
        {content}
      </form>
    );
  }

  return (
    <FormProvider context={form.context}>
      <FormLevelErrorContext.Provider value={error}>{formElement}</FormLevelErrorContext.Provider>
    </FormProvider>
  );
}

/** Provides a form-level error to `FormError` for forms built with `useZodForm` + `FormProvider`. */
export const FormLevelErrorProvider = FormLevelErrorContext.Provider;
